import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from "@/components/ui/input-otp";
import { Phone, Mail, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import PhygitalIcon from "@/components/PhygitalIcon";
import { useCopyRegistry } from "@/hooks/useCopyRegistry";

/**
 * Phygital Login Page
 * - Email magic link is PRIMARY (required, Beehiiv-connected)
 * - Google Auth is alternative
 * - SMS Phone OTP is for RETURNING USERS ONLY (must already have an account with verified email + linked phone)
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const { get: c } = useCopyRegistry("public");

  // Method: 'email' (default), 'phone' (returning users only)
  const [loginMethod, setLoginMethod] = useState("email");

  // Step: 'entry' | 'otp' | 'email-sent'
  const [step, setStep] = useState("entry");

  // Form state
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [magicLinkDebug, setMagicLinkDebug] = useState(null);

  useEffect(() => {
    document.title = "phygital - Sign In";
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const redirect = localStorage.getItem("redirect_after_login");
      if (redirect) {
        localStorage.removeItem("redirect_after_login");
        navigate(redirect);
      } else {
        navigate("/me");
      }
    }
  }, [isAuthenticated, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  // Format phone display
  const formatPhone = (value) => {
    const d = value.replace(/\D/g, "");
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  // -------------------- EMAIL MAGIC LINK (primary) --------------------
  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/send-magic-link`, {
        email: email.trim().toLowerCase(),
        redirect_url: "/me"
      });
      if (res.data.success) {
        setStep("email-sent");
        setResendCooldown(30);
        toast.success("Magic link sent! Check your email.");
        if (res.data.debug_link) {
          setMagicLinkDebug(res.data.debug_link);
          console.log("Dev magic link:", res.data.debug_link);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- GOOGLE (Emergent-managed) --------------------
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/me";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // -------------------- PHONE OTP (returning users only) --------------------
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      // Backend will reject if user with this phone doesn't exist
      const checkRes = await axios.post(`${API}/auth/check-phone`, { phone: digits });
      if (!checkRes.data.exists) {
        toast.error("No account found for this number. Please sign in with email first.");
        setLoading(false);
        return;
      }
      const otpRes = await axios.post(`${API}/auth/send-otp`, { phone: digits, returning_only: true });
      if (otpRes.data.success) {
        setStep("otp");
        setResendCooldown(30);
        if (otpRes.data.dev_hint) {
          toast.success(otpRes.data.dev_hint);
        } else {
          toast.success("Verification code sent!");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (otpValue) => {
    if (otpValue.length !== 6) return;
    setLoading(true);
    const digits = phone.replace(/\D/g, "");
    try {
      const res = await axios.post(`${API}/auth/verify-otp`, {
        phone: digits,
        otp: otpValue,
      });
      login(res.data);
      toast.success("Welcome back!");
      navigate("/me");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    const digits = phone.replace(/\D/g, "");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { phone: digits, returning_only: true });
      setResendCooldown(30);
      toast.success("New code sent!");
    } catch {
      toast.error("Failed to resend code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendMagic = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-magic-link`, { email: email.trim().toLowerCase(), redirect_url: "/me" });
      setResendCooldown(30);
      toast.success("New magic link sent!");
    } catch {
      toast.error("Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("entry");
    setOtp("");
    setMagicLinkDebug(null);
  };

  // -------------------- RENDER --------------------
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 relative" data-testid="login-page">
      {/* Subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-b from-gray-50 to-transparent rounded-full blur-3xl opacity-60" />
      </div>

      {/* Back button on inner steps */}
      {step !== "entry" && (
        <button
          onClick={handleBack}
          className="absolute top-6 left-6 flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      )}

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <PhygitalIcon className="w-14 h-14" variant="dark" />
        </div>

        {/* ENTRY STEP */}
        {step === "entry" && loginMethod === "email" && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {c("login_page.hero_title", "Welcome to Phygital")}
              </h1>
              <p className="text-sm text-gray-500">
                {c("login_page.hero_subtitle", "Your preferences. Your experience.")}
              </p>
            </div>

            {/* Primary: Email magic link */}
            <form onSubmit={handleSendMagicLink} className="space-y-3" data-testid="email-login-form">
              <div>
                <Label htmlFor="email" className="text-xs font-medium text-gray-700">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 mt-1 rounded-full border-gray-200 bg-gray-50 px-5 text-sm focus:border-gray-400 focus:ring-gray-400"
                  data-testid="email-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !email.includes("@")}
                className="w-full h-12 rounded-full bg-black hover:bg-gray-800 text-white font-medium"
                data-testid="email-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending link...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Continue with Email
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Secondary: Google */}
            <Button
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full h-12 rounded-full border-gray-200 hover:bg-gray-50 font-medium"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            {/* Tertiary: Phone (returning users only) */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setLoginMethod("phone")}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
                data-testid="switch-to-phone-btn"
              >
                <Phone className="w-3 h-3" />
                Returning user? Sign in with phone
              </button>
            </div>

            {/* Terms single line, compact */}
            <p className="text-center text-[10px] text-gray-400 mt-8 whitespace-nowrap overflow-hidden text-ellipsis">
              By continuing, you agree to{" "}
              <Link to="/terms" className="underline hover:text-gray-700">Terms</Link>,{" "}
              <Link to="/privacy" className="underline hover:text-gray-700">Privacy</Link>,{" "}
              <a href="https://www.beehiiv.com/tou" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Beehiiv TOU</a> &{" "}
              <a href="https://www.beehiiv.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-gray-700">Beehiiv Privacy</a>
            </p>
          </>
        )}

        {/* PHONE ENTRY (returning users) */}
        {step === "entry" && loginMethod === "phone" && (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-[11px] text-gray-600 uppercase tracking-wider mb-3">
                <Phone className="w-3 h-3" /> Returning Users Only
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Sign in with phone
              </h1>
              <p className="text-sm text-gray-500">
                You must already have a Phygital account with a linked phone number.{" "}
                <button onClick={() => setLoginMethod("email")} className="text-gray-900 underline hover:no-underline">
                  New here? Use email
                </button>
              </p>
            </div>

            <form onSubmit={handlePhoneSubmit} className="space-y-3" data-testid="phone-login-form">
              <div>
                <Label htmlFor="phone" className="text-xs font-medium text-gray-700">
                  Phone number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  className="h-12 mt-1 rounded-full border-gray-200 bg-gray-50 px-5 text-sm focus:border-gray-400 focus:ring-gray-400"
                  maxLength={14}
                  data-testid="phone-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading || phone.replace(/\D/g, "").length < 10}
                className="w-full h-12 rounded-full bg-black hover:bg-gray-800 text-white font-medium"
                data-testid="phone-submit-btn"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending code...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Send code
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setLoginMethod("email")}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
                data-testid="switch-to-email-btn"
              >
                <Mail className="w-3 h-3" />
                Sign in with email instead
              </button>
            </div>
          </>
        )}

        {/* OTP STEP */}
        {step === "otp" && (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Enter code</h1>
              <p className="text-sm text-gray-500">We sent a verification code to {phone}</p>
            </div>

            <div className="flex justify-center mb-6">
              <InputOTP
                maxLength={6}
                value={otp}
                onChange={(value) => {
                  setOtp(value);
                  if (value.length === 6) handleVerifyOtp(value);
                }}
                disabled={loading}
                data-testid="otp-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {loading && (
              <div className="flex justify-center mb-3">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            <div className="text-center">
              <button
                onClick={handleResendOtp}
                disabled={resendCooldown > 0 || loading}
                className={`text-xs ${resendCooldown > 0 ? "text-gray-400" : "text-gray-700 hover:underline"}`}
                data-testid="resend-otp-btn"
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
              </button>
            </div>
          </>
        )}

        {/* EMAIL SENT STEP */}
        {step === "email-sent" && (
          <div className="text-center" data-testid="email-sent-step">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-5">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your email</h1>
            <p className="text-sm text-gray-500 mb-1">
              We sent a magic link to <span className="font-medium text-gray-700">{email}</span>
            </p>
            <p className="text-xs text-gray-400 mb-6">The link expires in 1 hour</p>

            {magicLinkDebug && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                <p className="text-[10px] font-medium text-yellow-800 mb-1 uppercase tracking-wider">Dev Mode</p>
                <a href={magicLinkDebug} className="text-xs text-yellow-900 underline break-all" data-testid="dev-magic-link">
                  {magicLinkDebug}
                </a>
              </div>
            )}

            <Button
              onClick={handleResendMagic}
              disabled={loading || resendCooldown > 0}
              variant="outline"
              className="w-full h-11 rounded-full border-gray-200"
              data-testid="resend-magic-btn"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend link"}
            </Button>
            <button
              onClick={handleBack}
              className="block w-full text-xs text-gray-500 hover:text-gray-900 mt-3"
            >
              Try a different method
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
